import { erApiFeil } from '../api/feil.ts';
import { andel, useStats } from '../api/useStats.ts';
import { IKKE_NOK } from '../programmer/Rapport.tsx';
import { Ikon } from '../status/Ikon.tsx';

const uke = (iso: string) => new Date(iso).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' });

/** Medianen til første status: timer under to døgn, dager med én desimal over (A9). */
export function medianTekst(timer: number): string {
  return timer < 48 ? `${timer} t` : `${(timer / 24).toLocaleString('nb-NO', { maximumFractionDigits: 1 })} dager`;
}

/**
 * Statusflyt «inn/ut per status per uke» (M4.2, rapport 6 A11 — UserVoice Activity Report),
 * pluss de to løftene som bor her: «svaret var verdt noe» (gjentakende innsendere etter svar)
 * og «40 saker på en time» (saker per økt). Tellinger fra `GET /v1/admin/stats`; andelene
 * regnes her, og aldri under 30 i nevneren (DD-16) — da står IKKE_NOK.
 *
 * Medianen til første status bor her fra 2026-09-19: den sto i innboksens topp, som nå har
 * to tall og bare filtre (R1.3). Den er produkteierens tall, ikke saksbehandlerens — samme
 * spørring som resten av rapporten, ingen ny.
 */
export function Statusflyt({ weeks = 12 }: { weeks?: number }) {
  const stats = useStats(weeks);

  return (
    <details className="panel-statusflyt" data-testid="statusflyt">
      <summary className="panel-h3">
        <Ikon navn="swap-horizontal-outline" /> Statusflyt — inn og ut per uke, siste {weeks} uker
      </summary>
      {stats.isPending ? (
        <p role="status" className="panel-tekst-liten">
          Henter tallene …
        </p>
      ) : stats.isError ? (
        <p role="alert" className="panel-melding panel-melding-feil">
          <Ikon navn="warning-outline" /> Kunne ikke hente tallene. {erApiFeil(stats.error) ? stats.error.brukertekst : ''}
        </p>
      ) : (
        <>
          <table className="panel-tabell" aria-label="Statusflyt per uke">
            <thead>
              <tr>
                <th scope="col">Uke fra</th>
                <th scope="col">Inn</th>
                <th scope="col">Levert</th>
                <th scope="col">Avvist</th>
                <th scope="col">Duplikat</th>
                <th scope="col">Kjent feil</th>
                <th scope="col">Åpne ved ukeslutt</th>
              </tr>
            </thead>
            <tbody>
              {stats.data.flow.map((u) => (
                <tr key={u.weekStart} data-testid={`flyt-${u.weekStart.slice(0, 10)}`}>
                  <th scope="row">{uke(u.weekStart)}</th>
                  <td>{u.received}</td>
                  <td>{u.levert}</td>
                  <td>{u.avvist}</td>
                  <td>{u.duplikat}</td>
                  <td>{u.kjentFeil}</td>
                  <td>{u.openAtEnd}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <dl className="panel-prg-tellinger" data-testid="statusflyt-loefter">
            <Rad navn="Svar innen fristen" verdi={`${stats.data.promise.repliedWithinDeadline} av ${stats.data.promise.repliedWithinDeadline + stats.data.promise.repliedAfterDeadline}`} />
            <Rad
              navn="Første status"
              verdi={stats.data.promise.medianHoursToFirstStatus != null ? `median ${medianTekst(stats.data.promise.medianHoursToFirstStatus)}` : 'Ingen ennå'}
              hint={stats.data.promise.medianHoursToFirstStatus != null ? undefined : IKKE_NOK}
              testId="statusflyt-median"
            />
            <Rad navn="Sendte igjen etter svar" verdi={`${stats.data.repeat.sentAgain} av ${stats.data.repeat.replied}`} hint={andel(stats.data.repeat.sentAgain, stats.data.repeat.replied) ?? IKKE_NOK} testId="statusflyt-gjentakende" />
            <Rad navn="Saker per økt" verdi={stats.data.throughput.sessions > 0 ? `${stats.data.throughput.casesHandled} saker · ${stats.data.throughput.sessions} økter` : 'Ingen økter ennå'} hint={stats.data.throughput.medianSecondsPerCase != null ? `median ${Math.round(stats.data.throughput.medianSecondsPerCase / 60)} min per sak` : IKKE_NOK} testId="statusflyt-oekter" />
          </dl>
        </>
      )}
    </details>
  );
}

function Rad({ navn, verdi, hint, testId }: { navn: string; verdi: string; hint?: string; testId?: string }) {
  return (
    <div className="panel-prg-telling" data-testid={testId}>
      <dt className="panel-etikett">{navn}</dt>
      <dd className="panel-prg-tall">
        {verdi}
        {hint ? <span className="panel-etikett"> — {hint}</span> : null}
      </dd>
    </div>
  );
}
